import { useCallback, useMemo, useRef } from 'react';
import { useForm } from '@tanstack/react-form';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { mapServerValidationErrors, type ServerFieldMap } from '@/shared/forms';
import { useSaveInventoryAirlineBaggageRule } from '@/api/generated/inventory-airlines/inventory-airlines';
import { invalidateAirline } from '../internal/airlinesQueryKeys';
import {
  buildBaggageRuleFormSchema,
  toBaggageRuleRequestBody,
  type BaggageRuleFormValues,
} from './baggageRuleFormSchema';

/** Mapování názvů polí z BE validace na cesty polí ve formuláři. */
const SERVER_FIELD_MAP: ServerFieldMap = {
  Category: 'category',
  Name: 'name',
  Description: 'description',
  WidthCm: 'widthCm',
  HeightCm: 'heightCm',
  DepthCm: 'depthCm',
  WeightKg: 'weightKg',
  IsIncluded: 'pricing',
  MaxPiecesPerPassenger: 'maxPiecesPerPassenger',
  // Tři `bool` sloupce mají ve formuláři jedno pole — validátor jim proto dává vlastní jméno.
  PurchaseChannels: 'purchaseChannels',
  PriceFrom: 'priceFrom',
  PriceTo: 'priceTo',
};

interface UseBaggageRuleFormOptions {
  airlineId: number;
  initialValues: BaggageRuleFormValues;
  /** Persistovaná měna dopravce — bez ní nelze uložit pravidlo s cenou. */
  hasCarrierCurrency: boolean;
  /** Voláno po úspěšném uložení — typicky zavře dialog a zobrazí toast. */
  onSaved: () => void;
  /** Voláno při chybě, kterou se nepodařilo namapovat na pole. */
  onError?: (error: unknown) => void;
}

/**
 * Formulář dialogu pravidla zavazadla: Zod + upsert mutace + mapování BE chyb. Pravidlo se ukládá
 * **okamžitě vlastním endpointem**, nezávisle na formuláři letecké společnosti, takže si invalidaci
 * detailu i seznamu řeší samo — mění se obsah detailu i počet pravidel v seznamu.
 * `submit()` = úspěch uložení.
 */
export function useBaggageRuleForm({
  airlineId,
  initialValues,
  hasCarrierCurrency,
  onSaved,
  onError,
}: UseBaggageRuleFormOptions) {
  const { t } = useTranslation('inventory');
  const queryClient = useQueryClient();
  const schema = useMemo(
    () => buildBaggageRuleFormSchema(t, hasCarrierCurrency),
    [t, hasCarrierCurrency]
  );
  const saveMutation = useSaveInventoryAirlineBaggageRule();
  const successRef = useRef(false);

  const form = useForm({
    defaultValues: initialValues,
    validators: { onChange: schema },
    onSubmit: async ({ value, formApi }) => {
      successRef.current = false;
      try {
        await saveMutation.mutateAsync({ id: airlineId, data: toBaggageRuleRequestBody(value) });
        invalidateAirline(queryClient, airlineId);
        successRef.current = true;
        onSaved();
      } catch (error) {
        const mapped = mapServerValidationErrors(formApi, error, SERVER_FIELD_MAP);
        if (!mapped) onError?.(error);
      }
    },
  });

  const submit = useCallback(async () => {
    await form.handleSubmit();
    return successRef.current;
  }, [form]);

  return { form, submit, isSubmitting: saveMutation.isPending };
}
